/**
 * MobileActionOverlay — full-screen action form wrapper for mobile.
 *
 * Extracted as a separate React.memo component to prevent re-renders
 * from parent timer ticks (setRemaining every 1s) which caused iOS
 * keyboard dismissal on input focus.
 *
 * The parent ParticipantDashboard re-renders every second due to the
 * countdown timer. Without memo, the IIFE overlay re-executed each
 * second, causing React to reconcile the ActionForm tree, which on
 * iOS Safari could steal focus from inputs and dismiss the keyboard.
 */

import { memo } from 'react'
import { ActionForm } from '@/components/participant/ActionForms'

// Action categories for label lookup
const ACTION_LABELS: Record<string, string> = {
  public_statement: 'Public Statement',
  invite_to_meet: 'Set Meetings', call_org_meeting: 'Call Org Meeting',
  set_budget: 'National Budget', set_tariffs: 'Set Tariffs',
  set_sanctions: 'Set Sanctions', set_opec: 'Set Cartel Production',
  propose_transaction: 'Propose Transaction', propose_agreement: 'Propose Agreement',
  basing_rights: 'Basing Rights', declare_war: 'Declare War',
  attack: 'Attack', naval_blockade: 'Naval Blockade', move_units: 'Move Units',
  martial_law: 'Martial Law', nuclear_test: 'Nuclear Test',
  nuclear_launch_initiate: 'Nuclear Launch',
  reassign_types: 'Reassign Powers', arrest: 'Arrest',
  change_leader: 'Change Leader', self_nominate: 'Self-Nominate',
  cast_vote: 'Cast Vote', intelligence: 'Intelligence',
  covert_operation: 'Covert Operation', assassination: 'Assassination',
}

interface Props {
  actionType: string
  roleId: string
  roleName: string
  countryId: string
  simId: string
  onClose: () => void
  onSubmitted: () => void
}

export const MobileActionOverlay = memo(function MobileActionOverlay({
  actionType, roleId, roleName, countryId, simId, onClose, onSubmitted,
}: Props) {
  const actionLabel = ACTION_LABELS[actionType] || actionType

  return (
    <div className="fixed inset-0 z-[55] bg-base flex flex-col">
      {/* Sticky header — always visible */}
      <div className="flex-shrink-0 px-3 pt-3 pb-2 bg-base border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-h2 text-text-primary">{actionLabel}</h2>
          <button onClick={onClose}
            className="font-body text-body-sm text-text-secondary hover:text-text-primary px-3 py-2 rounded border border-border hover:border-action/30 min-h-[44px]">
            ← Back to Actions
          </button>
        </div>
      </div>
      {/* Scrollable form — form's own header hidden via CSS */}
      <div className="flex-1 overflow-y-auto px-3 pb-24" style={{ WebkitOverflowScrolling: 'touch' as never }}>
        <div className="mobile-form-wrapper">
          <ActionForm
            actionType={actionType}
            roleId={roleId}
            roleName={roleName}
            countryId={countryId}
            simId={simId}
            onClose={onClose}
            onSubmitted={onSubmitted}
            isMobile
          />
        </div>
      </div>
    </div>
  )
})
